import axios, { AxiosError } from "axios";
import { noop } from "lodash";

import { createHmacSignature } from "../shared/utils";

import {
  DataToSign,
  LinkTokenResponse,
  SaveAccessTokenPayload,
  SignUpResponse,
  TermsAndPrivacyResponse,
} from "./fkc.types";

export interface UserData {
  publisher_user_id: string;
  is_verified: boolean;
  cards_count: number;
}

const generateAuthorization = (data: DataToSign = {}) => ({
  Authorization: createHmacSignature(data, window.FKL.cashBackSecretKey),
});

export namespace FKCService {
  export function getTermsAndPrivacy(): Promise<TermsAndPrivacyResponse> {
    return axios
      .get<TermsAndPrivacyResponse>(
        `${window.FKL.yentaServicesApiUrl}/cash-back/terms`,
        {
          headers: generateAuthorization(),
        }
      )
      .then((response) => response.data)
      .catch((error) => {
        throw new Error(
          `Failed to fetch terms and privacy: ${
            error.response?.data?.message ?? error.message
          }`
        );
      });
  }

  export function signUp(email: string, password: string): Promise<string> {
    const payload = {
      email,
      password,
    };

    return axios
      .post<SignUpResponse>(
        `${window.FKL.yentaServicesApiUrl}/cash-back/sign-up`,
        payload,
        { headers: generateAuthorization({ payload: JSON.stringify(payload) }) }
      )
      .then((response) => response.data.publisher_user_id)
      .catch((error) => {
        throw new Error(
          `Failed to sign up: ${error.response?.data?.message ?? error.message}`
        );
      });
  }

  export function acceptTermsAndConditions(
    publisherUserId: string,
    tncId: number
  ): Promise<void> {
    const payload = {
      publisher_user_id: publisherUserId,
      tnc_id: tncId,
    };

    return axios
      .post(
        `${window.FKL.yentaServicesApiUrl}/cash-back/terms/accept`,
        payload,
        { headers: generateAuthorization({ payload: JSON.stringify(payload) }) }
      )
      .then(noop)
      .catch((error) => {
        throw new Error(
          `Failed to accept terms and conditions: ${
            error.response?.data?.message ?? error.message
          }`
        );
      });
  }

  export function verifyAccount(
    email: string,
    otp: string,
    handleWidgetError: React.Dispatch<React.SetStateAction<Error | null>>
  ): Promise<void> {
    const payload = {
      email,
      verification_code: otp,
    };

    return axios
      .post(`${window.FKL.yentaServicesApiUrl}/cash-back/verify`, payload, {
        headers: generateAuthorization({ payload: JSON.stringify(payload) }),
      })
      .then(noop)
      .catch((error: AxiosError) => {
        if (error.response?.status !== 400) {
          handleWidgetError(error);
        }

        throw new Error(
          `Invalid verification code, redirecting to retry page:
          ${error.response?.data?.message ?? error.message}`
        );
      });
  }

  export function resendVerificationCode(email: string): Promise<void> {
    const payload = {
      email,
    };

    return axios
      .post(
        `${window.FKL.yentaServicesApiUrl}/cash-back/resend-verification`,
        payload,
        { headers: generateAuthorization({ payload: JSON.stringify(payload) }) }
      )
      .then(noop)
      .catch((error) => {
        throw new Error(
          `Failed to resend verification code: ${
            error.response?.data?.message ?? error.message
          }`
        );
      });
  }

  export function getLinkToken(publisherUserId: string): Promise<string> {
    const params = {
      publisher_user_id: publisherUserId,
    };

    return axios
      .get<LinkTokenResponse>(
        `${window.FKL.yentaServicesApiUrl}/cash-back/plaid/link-token`,
        { params, headers: generateAuthorization({ params }) }
      )
      .then((response) => response.data.link_token)
      .catch((error) => {
        throw new Error(
          `Failed to get link token: ${
            error.response?.data?.message ?? error.message
          }`
        );
      });
  }

  export function savePublicToken(
    publisher_user_id: string,
    payload: SaveAccessTokenPayload
  ): Promise<void> {
    return axios
      .post(
        `${window.FKL.yentaServicesApiUrl}/cash-back/plaid/public-token/${publisher_user_id}`,
        payload,
        { headers: generateAuthorization({ payload: JSON.stringify(payload) }) }
      )
      .then(noop)
      .catch((error) => {
        throw new Error(
          `Failed to save public token: ${
            error.response?.data?.message ?? error.message
          }`
        );
      });
  }

  export async function getUser(email: string): Promise<UserData | null> {
    const params = { email };

    return axios
      .get<UserData>(`${window.FKL.yentaServicesApiUrl}/cash-back/user`, {
        params,
        headers: generateAuthorization({ params }),
      })
      .then(({ data, status }) => {
        if (status === 200) {
          return data;
        }

        throw new Error(
          `Failed to get user. Unhandled success status: ${status}`
        );
      })
      .catch((error: AxiosError) => {
        const status = error.response?.status;

        if (status === 404) {
          return null;
        }

        throw new Error(
          `Failed to get user: ${status ?? "Unknown"} ${error.message}`
        );
      });
  }
}
